import React, { createContext, useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import {
  UseMutateAsyncFunction,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";
import * as Sentry from "@sentry/react";
import Loading from "src/components/Loading";
import { useQueryStringRedirect } from "src/utils/hooks";
import { Intercom, Mixpanel } from "src/utils/analytics";
import queryKeys from "src/api/queryKeys";
import { logout } from "src/api/session";

export type AuthState = {
  isAuthenticated: boolean;
  login: UseMutateAsyncFunction<unknown, unknown, any>;
  isLoginLoading: boolean;
  logout: UseMutateAsyncFunction;
  isLogoutLoading: boolean;
  cleanupAfterLogout: () => void;
  refetchMe: () => Promise<void>;
};

type AuthProviderProps = {
  children: React.ReactNode;
};

type CreateAuthProviderProps<TAuthState, TCurrentUser> = {
  loginSuccessUrl: string;
  logoutSuccessUrl: string;
  loginFn: UseMutateAsyncFunction<unknown, unknown, any>;
  transformCurrentUserToContextValue: (arg: TCurrentUser) => TAuthState;
  getMeFn: () => Promise<TCurrentUser>;
};

const createAuthProvider = <TAuthState, MeData>({
  loginSuccessUrl,
  logoutSuccessUrl,
  loginFn,
  transformCurrentUserToContextValue,
  getMeFn,
}: CreateAuthProviderProps<TAuthState, MeData>) => {
  const AuthContext = createContext<(TAuthState & AuthState) | null>(null);

  const AuthProvider = ({ children }: AuthProviderProps) => {
    const history = useHistory();
    const queryClient = useQueryClient();
    const redirect = useQueryStringRedirect();

    const { status, data } = useQuery(queryKeys.me, getMeFn, {
      retry: false,
    });

    const login = useMutation(loginFn, {
      onSuccess: async () => {
        await queryClient.invalidateQueries([queryKeys.me]);
        redirect(loginSuccessUrl);
      },
    });

    const executeLogout = useMutation(logout, {
      onSuccess: async () => {
        await cleanupAfterLogout();
      },
      onError: (error) => {
        console.log(error);
      },
    });

    const cleanupAfterLogout = useCallback(async () => {
      queryClient.clear();

      history.push(logoutSuccessUrl);
      Intercom.shutdown();

      Intercom.boot();

      Mixpanel.reset();

      Sentry.configureScope((scope) => scope.setUser(null));
    }, [history, queryClient]);

    const refetchMe = useCallback(async () => {
      await queryClient.invalidateQueries([queryKeys.me]);
    }, [queryClient]);

    const value = useMemo(() => {
      const userData =
        data && transformCurrentUserToContextValue(data as MeData);
      return {
        ...(userData as TAuthState),
        isAuthenticated: !!data,
        login: login.mutateAsync,
        isLoginLoading: login.isLoading,
        logout: executeLogout.mutateAsync,
        isLogoutLoading: executeLogout.isLoading,
        cleanupAfterLogout,
        refetchMe,
      };
    }, [login, executeLogout, data, cleanupAfterLogout, refetchMe]);

    if (status === "idle") {
      return <Loading />;
    }

    if (status === "error") {
      return (
        <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
      );
    }

    if (status === "loading") {
      return <Loading />;
    }

    if (status === "success") {
      return data ? (
        <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
      ) : (
        <Loading />
      );
    }

    throw new Error(`AuthProvider has invalid status: ${status}`);
  };

  const useAuth = () => {
    const context = React.useContext(AuthContext);

    if (context === undefined) {
      throw new Error("`useAuth` must be used within a `AuthProvider`");
    }

    return context;
  };

  return { useAuth, AuthProvider };
};

export { createAuthProvider };
